import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import {
Box,
Typography,
Button,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
TextField,
Table,
TableHead,
TableBody,
TableRow,
TableCell,
TableContainer,
Chip,
MenuItem,
Select,
FormControl,
InputLabel,
Alert,
CircularProgress,
IconButton,
Autocomplete,
Card,
CardContent,
Grid,
Pagination,
Stack,
Paper,
} from '@mui/material';
import DataTable from '../components/DataTable';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import html2pdf from 'html2pdf.js';
import backendApi from '../shared/api/backendApi';
import {
  getInvoiceHtml2PdfOptions,
  getInvoicePrintCss,
  getStoredInvoicePrintProfile,
  normalizeInvoicePrintProfile,
  resolveInvoicePrintProfile
} from '../shared/printProfile';

// Memoized component for total sales value
const TotalSalesValue = React.memo(({ totalValue, totalCount, searchTerm, filteredCount }) => {
  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        ₹{totalValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
        Total of all sales • {totalCount} total sales{searchTerm ? ` (${filteredCount} filtered)` : ''}
      </Typography>
    </Box>
  );
});
TotalSalesValue.displayName = 'TotalSalesValue';

// Memoized component for formatted amount display
const FormattedAmount = React.memo(({ amount, variant = 'body2', sx = {} }) => {
  const formatted = useMemo(() => {
    const num = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : parseFloat(amount);
    return `₹${(isNaN(num) ? 0 : num).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }, [amount]);

  return (
    <Typography variant={variant} sx={sx}>
      {formatted}
    </Typography>
  );
});
FormattedAmount.displayName = 'FormattedAmount';

// Sales table columns configuration
const SALES_COLUMNS = [
  { key: 'invoiceNo', label: 'Invoice No', width: '10%' },
  { key: 'date', label: 'Date', width: '10%' },
  { key: 'items', label: 'Items', width: '15%' },
  { key: 'customer', label: 'Customer', width: '15%' },
  { key: 'phone', label: 'Phone', width: '12%' },
  { key: 'amount', label: 'Amount', width: '15%' },
  { key: 'payment', label: 'Payment', width: '15%' },
  { key: 'actions', label: 'Actions', width: '8%' },
];

// Memoized component for sales table row
const SalesRow = React.memo(({ sale, index, onEdit, onGenerateInvoice, rowIndex, navigate, isGeneratingInvoice, disableInvoiceGeneration }) => {
  const finalAmount = useMemo(() => {
    const amount = sale['Final Amount'] || sale['Total Amount'] || 0;
    const num = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : parseFloat(amount);
    return isNaN(num) ? 0 : num;
  }, [sale['Final Amount'], sale['Total Amount']]);

  const totalAmount = useMemo(() => {
    const amount = sale['Total Amount'] || 0;
    const num = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : parseFloat(amount);
    return isNaN(num) ? 0 : num;
  }, [sale['Total Amount']]);

  const totalPaid = useMemo(() => {
    const amount = sale['Total Paid'] || 0;
    const num = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : parseFloat(amount);
    return isNaN(num) ? 0 : num;
  }, [sale['Total Paid']]);

  const dueAmount = useMemo(() => {
    const amount = sale['Due Amount'] || 0;
    const num = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : parseFloat(amount);
    return isNaN(num) ? 0 : num;
  }, [sale['Due Amount']]);

  const items = useMemo(() => sale.items || [], [sale.items]);

  return (
    <TableRow
      key={sale.id || `sale-${index}`}
      sx={{
        backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc',
        '&:hover': { backgroundColor: '#e2e8f0' },
        minHeight: 80,
        '& .MuiTableCell-root': {
          padding: '8px 6px',
          overflow: 'visible',
          whiteSpace: 'normal'
        }
      }}
    >
      <TableCell sx={{ pr: 0.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            px: 0.5,
            py: 0.5,
            borderRadius: 1.5,
            display: 'inline-block',
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.3px',
            textAlign: 'center',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {sale['Invoice No']}
        </Box>
      </TableCell>
      <TableCell sx={{ pl: 0.5 }}>{sale['Date']}</TableCell>
      <TableCell>
        <Box sx={{ maxWidth: '100%' }}>
          {(() => {
            const displayItems = items.slice(0, 2);
            const remainingCount = items.length - 2;

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                {displayItems.map((item, i) => {
                  const quantity = parseFloat(item.quantity) || 1;
                  const itemName = item.item_name || '';
                  const shortName = itemName.length > 15 ? itemName.substring(0, 15) + '...' : itemName;
                  return (
                    <Chip
                      key={i}
                      label={`${shortName} (${quantity})`}
                      size="small"
                      sx={{
                        backgroundColor: '#333',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: '24px',
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          padding: '0 6px'
                        }
                      }}
                    />
                  );
                })}
                {remainingCount > 0 && (
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.5 }}>
                    +{remainingCount} more
                  </Typography>
                )}
              </Box>
            );
          })()}
        </Box>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '0.85rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {sale['Customer Name']}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '0.85rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {sale['Phone Number'] || 'N/A'}
        </Typography>
      </TableCell>
      <TableCell
        sx={{
          overflow: 'visible',
          whiteSpace: 'normal',
          verticalAlign: 'top',
          padding: '8px 6px !important',
          '& .MuiBox-root': {
            width: '100%',
            overflow: 'visible'
          }
        }}
      >
        <Box sx={{ width: '100%', overflow: 'visible' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.3 }}>
            FINAL AMOUNT
          </Typography>
          <FormattedAmount
            amount={finalAmount}
            variant="body2"
            sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.9rem', display: 'block' }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600, display: 'block', mt: 0.8, mb: 0.3 }}>
            TOTAL SELLING PRICE
          </Typography>
          <FormattedAmount
            amount={totalAmount}
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', display: 'block' }}
          />
        </Box>
      </TableCell>
      <TableCell
        sx={{
          overflow: 'visible',
          whiteSpace: 'normal',
          verticalAlign: 'top',
          padding: '8px 6px !important',
          '& .MuiBox-root': {
            width: '100%',
            overflow: 'visible'
          }
        }}
      >
        <Box sx={{ width: '100%', overflow: 'visible' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 700, display: 'block', mb: 0.3 }}>
            TOTAL PAID
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <FormattedAmount
              amount={totalPaid}
              variant="body2"
              sx={{ fontWeight: 700, color: 'success.main', fontSize: '0.9rem', display: 'block' }}
            />
            {sale.creditApplied && sale.creditApplied > 0 && (
              <Chip
                label={`+₹${sale.creditApplied}`}
                size="small"
                sx={{
                  backgroundColor: '#e8f5e8',
                  color: '#2e7d32',
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  height: '16px',
                  padding: '0 4px'
                }}
              />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 700, display: 'block', mt: 0.8, mb: 0.3 }}>
            TOTAL DUE
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: dueAmount > 0 ? 'error.main' : (dueAmount < 0 ? 'success.main' : 'text.primary'),
              fontWeight: 700,
              fontSize: '0.9rem',
              display: 'block'
            }}
          >
            <FormattedAmount amount={dueAmount} />
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ padding: '8px 4px !important' }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <IconButton
            onClick={() => onEdit(sale)}
            size="small"
            color="primary"
            title="Edit Sale"
            sx={{ padding: '4px' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => onGenerateInvoice(sale)}
            title={isGeneratingInvoice ? 'Generating invoice...' : 'Generate Invoice'}
            size="small"
            disabled={disableInvoiceGeneration}
            sx={{ padding: '4px' }}
          >
            {isGeneratingInvoice ? <CircularProgress size={16} /> : <ReceiptIcon fontSize="small" />}
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
});
SalesRow.displayName = 'SalesRow';

const SelectedItemRow = React.memo(({ item, onQuantityChange, onCustomerPriceChange, onRemove }) => {
  const salePrice = useMemo(() => parseFloat(item.unitPrice || 0), [item.unitPrice]);
  const customerPrice = useMemo(() => parseFloat(item.customerPrice || item.unitPrice || 0), [item.customerPrice, item.unitPrice]);
  const quantity = useMemo(() => parseFloat(item.quantity || 0), [item.quantity]);

  const discountPercent = useMemo(() => {
    if (salePrice > 0 && customerPrice < salePrice) {
      return `${((salePrice - customerPrice) / salePrice * 100).toFixed(2)}%`;
    }
    return '0.00%';
  }, [salePrice, customerPrice]);

  const lineTotal = useMemo(() => {
    return (quantity * customerPrice).toLocaleString('en-IN');
  }, [quantity, customerPrice]);

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {item.name}
        </Typography>
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          size="small"
          value={item.quantity || 1}
          onChange={(e) => onQuantityChange(item.id, e.target.value)}
          inputProps={{ min: 1, style: { textAlign: 'center' } }}
          sx={{ width: 80 }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          ₹{salePrice.toLocaleString('en-IN')}
        </Typography>
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          size="small"
          value={item.customerPrice || item.unitPrice || 0}
          onChange={(e) => onCustomerPriceChange(item.id, e.target.value)}
          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'center' } }}
          sx={{ width: 100 }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="error.main" sx={{ fontWeight: 500, textAlign: 'center' }}>
          {discountPercent}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
          ₹{lineTotal}
        </Typography>
      </TableCell>
      <TableCell>
        <IconButton
          size="small"
          onClick={() => onRemove(item.id)}
          color="error"
        >
          <ClearIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
});
SelectedItemRow.displayName = 'SelectedItemRow';


function Sales() {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]); // Array of item objects: {id, name, quantity, unitPrice, discount, taxRate}
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [editingSale, setEditingSale] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(debouncedSearchTerm);
  const [customers, setCustomers] = useState([]); // Array of customer objects
  const [selectedCustomerCredit, setSelectedCustomerCredit] = useState(0); // Available credit for selected customer
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceGeneratingSaleId, setInvoiceGeneratingSaleId] = useState(null);
  const [invoiceActionLoading, setInvoiceActionLoading] = useState({
    download: false,
    print: false,
    whatsapp: false
  });
  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    return localStorage.getItem('selectedInvoiceTemplate') || 'simple';
  });
  const [templateData, setTemplateData] = useState(() => {
    // Load from localStorage on initial render
    const saved = localStorage.getItem('invoiceTemplateData');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          printProfile: normalizeInvoicePrintProfile(parsed?.printProfile || getStoredInvoicePrintProfile())
        };
      } catch (parseError) {
        console.warn('Failed to parse invoiceTemplateData from localStorage, using defaults:', parseError);
      }
    }

    return {
      companyName: '',
      address: '',
      phone: '',
      email: '',
      gstNumber: '',
      bankDetails: '',
      terms: '',
      logo: null,
      // New invoice-specific fields
      taxRate: '0',
      salesNote: '',
      authorizedSignature: '',
      paymentTerms: 'Net 30', // Payment terms like "Net 30" (due in 30 days)
      printProfile: getStoredInvoicePrintProfile(),
      invoicePrefix: 'INV', // Invoice prefix (e.g., INV, INV001, or business initials)
    };
  });

  // Pagination state
  const [allSales, setAllSales] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalSalesCount, setTotalSalesCount] = useState(0);
  const [totalSalesValue, setTotalSalesValue] = useState(0);

  // Performance optimization states
  const [fullDataLoaded, setFullDataLoaded] = useState(false);
  const [loadingFullData, setLoadingFullData] = useState(false);
  const [fullSalesData, setFullSalesData] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const lastSearchQueryRef = useRef('');

  const invalidateSalesSearchCache = useCallback(() => {
    localStorage.removeItem('sales_full_data_cache');
    localStorage.removeItem('sales_full_data_timestamp');
    localStorage.removeItem('sales_inventory_cache');
    localStorage.removeItem('sales_customers_cache');
    localStorage.removeItem('sales_static_data_timestamp');
    setFullDataLoaded(false);
    setFullSalesData(null);
  }, []);

  // Save template selection to localStorage
  useEffect(() => {
    localStorage.setItem('selectedInvoiceTemplate', selectedTemplate);
  }, [selectedTemplate]);
  const [businessData, setBusinessData] = useState({
    companyName: 'Your Business Name',
    address: 'Your Business Address',
    phone: 'Your Phone',
    email: 'your@email.com',
    gstNumber: '',
    logoUrl: '',
    terms: 'Payment due within 30 days'
  });
  // Helper function to get current date in local timezone as YYYY-MM-DD
  const getCurrentLocalDate = () => {
    const now = new Date();
    // Adjust for timezone offset to get the correct local date
    const offset = now.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
    const localDate = new Date(now.getTime() - offset);
    return localDate.toISOString().slice(0, 10);
  };

  const [formData, setFormData] = useState({
    date: getCurrentLocalDate(),
    customerId: null,
    customerName: '',
    phoneNumber: '',
    notes: '',
    paymentMode: '',
    totalPaid: 0,
  });

  const normalizeSalesData = useCallback((salesData = []) => {
    return salesData.map((sale) => ({
      ...sale,
      'Final Amount': sale['Final Amount'] || sale['Total Amount'] || '0',
      'Total Amount': sale['Total Amount'] || '0',
      'Total Paid': sale['Total Paid'] || '0',
      'Due Amount': sale['Due Amount'] || '0',
      'Invoice No': sale['Invoice No'] || sale.invoice_no || '',
      'Date': sale['Date'] || sale.invoice_date || '',
      'Customer Name': sale['Customer Name'] || sale.customer_name || 'N/A',
      'Phone Number': sale['Phone Number'] || sale.customer_phone || '',
      items: sale.items || []
    }));
  }, []);

  useEffect(() => {
    loadStaticData();
    loadInitialSalesData();
    loadBusinessData();
    loadTotalSalesValue();
  }, []);

  // Debounce search term to reduce filter calculations
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle search term changes and trigger full data loading if needed
  useEffect(() => {
    const normalizedQuery = debouncedSearchTerm.trim().toLowerCase();
    setCurrentPage(1);

    if (!normalizedQuery) {
      lastSearchQueryRef.current = '';
      return;
    }

    const isNewQuery = lastSearchQueryRef.current !== normalizedQuery;
    lastSearchQueryRef.current = normalizedQuery;

    // Only trigger full-data loading for a new search query.
    if (isNewQuery && !fullDataLoaded && !loadingFullData) {
      loadFullSalesData();
    }
  }, [debouncedSearchTerm, fullDataLoaded, loadingFullData]);

  // Handle page changes - load data from backend when not searching
  const handlePageChange = async (event, newPage) => {
    console.log(`📄 Page change requested: ${currentPage} -> ${newPage}`);

    // If searching, pagination is handled client-side on already loaded data
    if (debouncedSearchTerm.trim()) {
      setCurrentPage(newPage);
      return;
    }

    // For non-search pagination, load data from backend
    try {
      setLoading(true);
      setError(null);

      const salesResponse = await backendApi.getSales(newPage, rowsPerPage);

      const salesData = salesResponse.sales || [];
      const totalSales = salesResponse.totalSales || 0;

      const normalizedSales = normalizeSalesData(salesData);

      console.log(`📊 Loaded page ${newPage} sales data: ${normalizedSales.length} sales (total: ${totalSales})`);

      setAllSales(normalizedSales);
      setCurrentPage(newPage);
      setTotalSalesCount(totalSales);
    } catch (err) {
      setError('Failed to load sales data for the selected page. Please try again.');
      console.error('Error loading page data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounce item search term
  const [debouncedItemSearchTerm, setDebouncedItemSearchTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedItemSearchTerm(itemSearchTerm);
    }, 200); // 200ms delay for item search

    return () => clearTimeout(timer);
  }, [itemSearchTerm]);

  // Load static data (inventory and customers) from backend (no local cache)
  const loadStaticData = async () => {
    console.time('loadStaticData');
    try {
      const customersResponse = await backendApi.getCustomers();
      const customersData = customersResponse.customers || [];
      setCustomers(customersData);
      console.log(`📦 Loaded ${customersData.length} customers from backend`);

      console.time('loadInventory');
      setInventoryLoading(true);
      try {
        const inventoryResponse = await backendApi.getInventory();
        const inventoryData = inventoryResponse.inventory || [];
        setInventory(inventoryData);
        console.log(`📦 Loaded ${inventoryData.length} inventory items from backend`);
      } finally {
        setInventoryLoading(false);
        console.timeEnd('loadInventory');
      }
    } catch (err) {
      console.error('Error in loadStaticData:', err);
    } finally {
      console.timeEnd('loadStaticData');
    }
  };

  // Load initial sales data (first page only for fast loading)
  const loadInitialSalesData = async () => {
    console.time('loadInitialSalesData');
    try {
      setLoading(true);
      setError(null);

      // Load only first page (50 records) for initial fast loading
      const salesResponse = await backendApi.getSales(1, 50);

      // Database API returns { success: true, sales: [...], totalSales: number, currentPage: number, totalPages: number, generatedAt: string }
      const salesData = salesResponse.sales || [];
      const totalSales = salesResponse.totalSales || 0;

      console.time('normalizeSales');
      const normalizedSales = normalizeSalesData(salesData);
      console.timeEnd('normalizeSales');

      console.log(`📊 Loaded initial sales data: ${normalizedSales.length} sales (total: ${totalSales})`);
      console.log(`⚡ Initial load performance: ${normalizedSales.length} records loaded for fast page display`);
      if (normalizedSales.length > 0) {
        console.log('📊 Sample sale:', {
          id: normalizedSales[0].id,
          invoiceNo: normalizedSales[0]['Invoice No'],
          finalAmount: normalizedSales[0]['Final Amount'],
          totalAmount: normalizedSales[0]['Total Amount'],
          totalPaid: normalizedSales[0]['Total Paid'],
          dueAmount: normalizedSales[0]['Due Amount']
        });
      }

      setAllSales(normalizedSales);
      setCurrentPage(1);
      setTotalSalesCount(totalSales);
    } catch (err) {
      setError('Failed to load sales data. Please try again.');
      console.error('Error loading sales data:', err);
    } finally {
      setLoading(false);
      console.timeEnd('loadInitialSalesData');
    }
  };

  // Load full sales data for search functionality
  const loadFullSalesData = async () => {
    console.time('loadFullSalesData');
    try {
      setLoadingFullData(true);

      // Load full dataset (all records)
      const salesResponse = await backendApi.getSales(1, 5000); // Load up to 5000 records for search

      const salesData = salesResponse.sales || [];
      const totalSales = salesResponse.totalSales || 0;

      const normalizedSales = normalizeSalesData(salesData);

      console.log(`📊 Loaded full sales data: ${normalizedSales.length} sales (total: ${totalSales})`);
      console.log(`🔍 Full data loaded for comprehensive search functionality`);

      setFullSalesData(normalizedSales);
      setFullDataLoaded(true);
      setTotalSalesCount(totalSales);
    } catch (err) {
      console.error('Error loading full sales data:', err);
      setError('Failed to load full sales data for search. Using initial data.');
    } finally {
      setLoadingFullData(false);
      console.timeEnd('loadFullSalesData');
    }
  };


  const loadBusinessData = async () => {
    try {
      console.log('🏢 Loading business data from database...');
      // Get business data from localStorage (set during authentication)
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedUserData = JSON.parse(userData);
        const businessDataStr = localStorage.getItem(`business_${parsedUserData.id}`);

        if (businessDataStr) {
          const businessInfo = JSON.parse(businessDataStr);
          console.log('📋 Business data from localStorage:', businessInfo);

          // Map to the expected business data structure
          const realBusinessData = {
            companyName: businessInfo.name || 'Your Business Name',
            name: businessInfo.name || 'Your Business Name',
            address: businessInfo.address || 'Your Business Address',
            phone: businessInfo.phone || 'Your Phone',
            businessPhone: businessInfo.phone || 'Your Phone',
            email: businessInfo.email || 'your@email.com',
            gstNumber: businessInfo.gstNumber || '',
            gst: businessInfo.gstNumber || '',
            businessType: businessInfo.businessType || '',
            type: businessInfo.businessType || '',
            industry: businessInfo.industry || '',
            website: businessInfo.website || '',
            city: businessInfo.city || '',
            state: businessInfo.state || '',
            zipCode: businessInfo.zipCode || '',
            country: businessInfo.country || 'India',
            description: businessInfo.description || '',
            employeeCount: businessInfo.employeeCount || '',
            foundedYear: businessInfo.foundedYear || '',
            logoUrl: '',
            terms: 'Payment due within 30 days'
          };

          console.log('✅ Real business data loaded:', realBusinessData);
          setBusinessData(realBusinessData);
        } else {
          console.log('⚠️ No business data found in localStorage');
        }
      } else {
        console.log('⚠️ No user data found in localStorage');
      }
    } catch (err) {
      console.error('❌ Error loading business data:', err);
      // Keep using sample data as fallback
      console.log('🔄 Using sample business data as fallback');
    }
  };

  // Load total sales value from analytics API
  const loadTotalSalesValue = async () => {
    try {
      console.log('📊 Loading total sales value from analytics...');
      const analyticsResponse = await backendApi.getDashboardMetrics();
      const totalSales = analyticsResponse.metrics?.totalSales || 0;
      setTotalSalesValue(totalSales);
      console.log('✅ Total sales value loaded:', totalSales);
    } catch (err) {
      console.error('❌ Error loading total sales value:', err);
      // Keep default value of 0
    }
  };

  // Memoized customer options for autocomplete to prevent unnecessary recalculations
  const customerById = useMemo(() => {
    const map = new Map();
    customers.forEach((customer) => {
      map.set(customer.id, customer);
    });
    return map;
  }, [customers]);

  const customersByNormalizedName = useMemo(() => {
    const map = new Map();
    customers.forEach((customer) => {
      const normalizedName = (customer.name || '').trim().toLowerCase();
      if (normalizedName) {
        map.set(normalizedName, customer);
      }
    });
    return map;
  }, [customers]);

  const selectedCustomerOption = useMemo(
    () => customerById.get(formData.customerId) || null,
    [customerById, formData.customerId]
  );

  const handleOpenDialog = (sale = null) => {
    if (sale) {
      // Editing existing sale
      console.log(`🎯 Editing sale with ID: ${sale.id}`);
      console.log('📊 Sale data:', sale);

      setEditingSale(sale);

      // Parse items from the sale data - database format
      console.log('🔍 Parsing sale data for editing:');
      console.log('🔍 Items:', sale.items);

      const parsedItems = [];
      if (sale.items) {
        sale.items.forEach((item) => {
          const quantity = parseFloat(item.quantity) || 1;
          const storedLineTotal =
            parseFloat(item.line_total ?? item.total ?? item.amount) || 0;

          // Prefer sale-time captured amount (line_total / qty) over mutable catalog fields.
          const customerPriceFromLine = quantity > 0 && storedLineTotal > 0
            ? storedLineTotal / quantity
            : 0;
          const customerPriceFromFields =
            parseFloat(item.customer_price ?? item.selling_price ?? item.unit_price ?? item.price ?? item.rate) || 0;
          const customerPrice = customerPriceFromLine > 0 ? customerPriceFromLine : customerPriceFromFields;

          const originalPriceCandidate =
            parseFloat(item.original_unit_price ?? item.mrp_price ?? item.mrp ?? item.list_price ?? item.unit_price) || 0;

          const rawDiscountPercent = parseFloat(item.discount);
          const safeRawDiscount = Number.isFinite(rawDiscountPercent) ? rawDiscountPercent : 0;
          const derivedDiscountPercent =
            originalPriceCandidate > 0 && customerPrice < originalPriceCandidate
              ? ((originalPriceCandidate - customerPrice) / originalPriceCandidate) * 100
              : 0;
          const discountPercent =
            safeRawDiscount >= 0 && safeRawDiscount <= 100 ? safeRawDiscount : derivedDiscountPercent;

          const originalSalePrice = Math.max(
            customerPrice,
            originalPriceCandidate > 0
              ? originalPriceCandidate
              : (discountPercent > 0 ? customerPrice / (1 - discountPercent / 100) : customerPrice)
          );

          parsedItems.push({
            id: item.item_id,
            name: item.item_name,
            quantity,
            unitPrice: originalSalePrice,
            customerPrice,
            discount: discountPercent
          });

          console.log('Parsed item for edit:', { name: item.item_name, quantity, originalSalePrice, customerPrice });
        });
      }

      console.log('🔍 Final parsed items:', parsedItems);

      const parseNumber = (value) => {
        const num = parseFloat(String(value ?? '').replace(/[^0-9.-]/g, ''));
        return Number.isFinite(num) ? num : 0;
      };

      const targetSaleTotal =
        parseNumber(sale['Total Amount']) ||
        parseNumber(sale.total_amount) ||
        parseNumber(sale['Final Amount']) ||
        parseNumber(sale.final_total);

      const parsedSubtotal = parsedItems.reduce((sum, item) => {
        return sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.customerPrice) || 0));
      }, 0);

      let normalizedItems = parsedItems;
      if (targetSaleTotal > 0 && parsedSubtotal > 0 && Math.abs(parsedSubtotal - targetSaleTotal) > 0.01) {
        if (parsedItems.length === 1) {
          const qty = parseFloat(parsedItems[0].quantity) || 1;
          const correctedCustomerPrice = targetSaleTotal / qty;
          normalizedItems = [
            {
              ...parsedItems[0],
              customerPrice: correctedCustomerPrice,
              unitPrice: Math.max(parseFloat(parsedItems[0].unitPrice) || 0, correctedCustomerPrice)
            }
          ];
        } else {
          const ratio = targetSaleTotal / parsedSubtotal;
          const scaled = parsedItems.map((item) => {
            const nextCustomerPrice = (parseFloat(item.customerPrice) || 0) * ratio;
            return {
              ...item,
              customerPrice: nextCustomerPrice,
              unitPrice: Math.max(parseFloat(item.unitPrice) || 0, nextCustomerPrice)
            };
          });

          const scaledTotal = scaled.reduce((sum, item) => sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.customerPrice) || 0)), 0);
          const delta = targetSaleTotal - scaledTotal;
          if (scaled.length > 0 && Math.abs(delta) > 0.0001) {
            const lastIndex = scaled.length - 1;
            const lastQty = parseFloat(scaled[lastIndex].quantity) || 1;
            scaled[lastIndex].customerPrice = (parseFloat(scaled[lastIndex].customerPrice) || 0) + (delta / lastQty);
            scaled[lastIndex].unitPrice = Math.max(parseFloat(scaled[lastIndex].unitPrice) || 0, scaled[lastIndex].customerPrice);
          }
          normalizedItems = scaled;
        }
      }

      console.log('Final parsed items for edit:', normalizedItems);
      setSelectedItems(normalizedItems);

      // Complex date parsing logic for editing existing sales
      let formattedDate = new Date().toISOString().slice(0,10);
      if (sale['Date']) {
        try {
          const dateStr = String(sale['Date']).trim();
          // Handle YYYY-MM-DD format from database
          if (dateStr.includes('-') && dateStr.length === 10) {
            formattedDate = dateStr;
          } else if (dateStr.includes('/')) {
            // Handle DD/MM/YYYY format
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              const day = parts[0].padStart(2, '0');
              const month = parts[1].padStart(2, '0');
              const year = parts[2];
              formattedDate = `${year}-${month}-${day}`;
            }
          } else if (dateStr.includes('-') && dateStr.length > 10) {
            // Handle ISO format with time
            const dateObj = new Date(dateStr);
            if (dateObj && !isNaN(dateObj.getTime())) {
              formattedDate = dateObj.toISOString().slice(0,10);
            }
          } else {
            // Try parsing other formats
            const dateObj = new Date(dateStr);
            if (dateObj && !isNaN(dateObj.getTime())) {
              formattedDate = dateObj.toISOString().slice(0,10);
            }
          }
        } catch (error) {
          console.warn('Error parsing date:', sale['Date'], error);
          // Keep default date
        }
      }

      // Customer ID lookup and credit balance setting
      const customer = customers.find(c => c.name === sale['Customer Name']);
      const customerId = customer ? customer.id : null;
      const customerCredit = customer ? parseFloat(customer.credit_balance || 0) : 0;
      setSelectedCustomerCredit(customerCredit);

      setFormData({
        date: formattedDate,
        customerId: customerId,
        customerName: sale['Customer Name'] || '',
        phoneNumber: sale['Phone Number'] || '',
        notes: '',
        paymentMode: sale['Payment Mode'] || '',
        totalPaid: parseFloat(sale['Total Paid'] || 0),
      });
    } else {
      // Adding new sale
      setEditingSale(null);
      setSelectedItems([]);
      setFormData({
        date: getCurrentLocalDate(),
        customerId: null,
        customerName: '',
        phoneNumber: '',
        notes: '',
        paymentMode: '',
        totalPaid: 0,
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingSale(null);
    setSelectedItems([]);
    setItemSearchTerm('');
    setSelectedCustomerCredit(0);
    setFormData({
      date: getCurrentLocalDate(),
      customerId: null,
      customerName: '',
      phoneNumber: '',
      notes: '',
      paymentMode: '',
      totalPaid: 0,
    });
  };

  // Memoized item search results
  const itemSearchResultsMemo = useMemo(() => {
    if (!debouncedItemSearchTerm || debouncedItemSearchTerm.length === 0) {
      return [];
    }
    const filtered = inventory
      .filter(item =>
        item.name?.toLowerCase().includes(debouncedItemSearchTerm.toLowerCase()) ||
        item.sku?.toLowerCase().includes(debouncedItemSearchTerm.toLowerCase())
      )
      .slice(0, 50); // Show up to 50 results, scrollable for better UX
    return filtered;
  }, [inventory, debouncedItemSearchTerm]);

  // Handle item search and filtering
  const handleItemSearch = useCallback((searchTerm) => {
    setItemSearchTerm(searchTerm);
  }, []);

  const handleItemSelect = useCallback((item) => {
    // Check if item is already selected by ID
    setSelectedItems(prev => {
      if (!prev.find(selected => selected.id === item.id)) {
        const salePrice = parseFloat(item.selling_price || 0);
        const newItem = {
          id: item.id,
          name: item.name,
          quantity: 1,
          unitPrice: salePrice, // Original sale price from inventory
          customerPrice: salePrice, // Customer price (starts same as sale price)
          discount: 0
        };
        return [...prev, newItem];
      }
      return prev;
    });
    // Clear search after selection
    setItemSearchTerm('');
  }, []);

  const handleItemRemove = useCallback((itemId) => {
    setSelectedItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const handleCustomerPriceChange = useCallback((itemId, value) => {
    const newPrice = parseFloat(value) || 0;
    setSelectedItems(prev => prev.map(i =>
      i.id === itemId ? { ...i, customerPrice: newPrice } : i
    ));
  }, []);

  const selectedItemIds = useMemo(() => {
    return new Set(selectedItems.map((item) => item.id));
  }, [selectedItems]);

  const handleQuantityChange = useCallback((itemId, quantity) => {
    setSelectedItems(prev => prev.map(item =>
      item.id === itemId
        ? { ...item, quantity: parseInt(quantity) || 0 }
        : item
    ));
  }, []);

  const handleItemDiscountChange = useCallback((itemId, discount) => {
    setSelectedItems(prev => prev.map(item =>
      item.id === itemId
        ? { ...item, discount: parseFloat(discount) || 0 }
        : item
    ));
  }, []);

  const renderSelectedItemRow = useCallback((item) => (
    <SelectedItemRow
      key={item.id}
      item={item}
      onQuantityChange={handleQuantityChange}
      onCustomerPriceChange={handleCustomerPriceChange}
      onRemove={handleItemRemove}
    />
  ), [handleQuantityChange, handleCustomerPriceChange, handleItemRemove]);


  // Memoized calculations for form summary
  const formCalculations = useMemo(() => {
    const total = selectedItems.reduce((sum, item) => {
      const customerPrice = parseFloat(item.customerPrice || item.unitPrice || 0);
      return sum + ((item.quantity || 0) * customerPrice);
    }, 0);

    const totalDiscount = selectedItems.reduce((sum, item) => {
      const salePrice = parseFloat(item.unitPrice || 0);
      const customerPrice = parseFloat(item.customerPrice || item.unitPrice || 0);
      const discountAmount = (salePrice - customerPrice) * (item.quantity || 0);
      return sum + Math.max(0, discountAmount);
    }, 0);

    const totalPaid = (formData.totalPaid || 0) + (formData.customerId ? selectedCustomerCredit : 0);
    const due = Math.max(0, total - totalPaid);

    return {
      total,
      totalDiscount,
      totalPaid,
      due
    };
  }, [selectedItems, formData.totalPaid, formData.customerId, selectedCustomerCredit]);


  const handleSubmit = async (mode = 'save') => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const shouldSendOnWhatsApp = mode === 'save_and_send' && !editingSale;

      // Validate form
      if (selectedItems.length === 0 || (!formData.customerId && !formData.customerName)) {
        setError('Please select items and enter customer details.');
        return;
      }

      // Prepare sale data with new structure
      const items = selectedItems.map(item => {
        const salePrice = parseFloat(item.unitPrice || 0);
        const customerPrice = parseFloat(item.customerPrice || item.unitPrice || 0);
        const discountPercent = salePrice > 0 ? ((salePrice - customerPrice) / salePrice * 100) : 0;

        return {
          itemId: item.id,
          quantity: item.quantity || 1,
          unitPrice: customerPrice,
          discount: discountPercent
        };
      });

      const totalAmount = selectedItems.reduce((sum, item) => {
        const customerPrice = parseFloat(item.customerPrice || item.unitPrice || 0);
        return sum + ((item.quantity || 0) * customerPrice);
      }, 0);
      const totalDiscount = selectedItems.reduce((sum, item) => {
        const salePrice = parseFloat(item.unitPrice || 0);
        const customerPrice = parseFloat(item.customerPrice || item.unitPrice || 0);
        const discountAmount = (salePrice - customerPrice) * (item.quantity || 0);
        return sum + Math.max(0, discountAmount);
      }, 0);
      const finalAmount = totalAmount;

      const saleData = {
        customerId: formData.customerId,
        customerName: formData.customerName,
        phoneNumber: formData.phoneNumber,
        date: formData.date,
        items,
        totalAmount,
        totalDiscount,
        finalAmount,
        totalPaid: formData.totalPaid || 0,
        paymentMode: formData.paymentMode,
        notes: formData.notes || '',
      };

      if (editingSale) {
        console.log('Updating existing sale');
        await backendApi.updateSale(editingSale.id, saleData);
        setSuccess('Sale updated successfully!');
      } else {
        console.log('Adding new sale');
        const saleResponse = await backendApi.addSale(saleData);
        const createdSaleId = Number(saleResponse?.sale?.id || saleResponse?.id || 0);
        const creditApplied = Number(saleResponse?.sale?.creditApplied || saleResponse?.creditApplied || 0);

        let successMessage = creditApplied > 0
          ? `Sale added successfully! Rs.${creditApplied} customer credit automatically applied to this sale.`
          : 'Sale added successfully!';

        if (shouldSendOnWhatsApp) {
          if (!createdSaleId) {
            throw new Error('Could not determine sale ID for WhatsApp sending.');
          }

          const selectedTemplateId = selectedTemplate || templateData.theme || 'simple';
          const printProfile = resolveInvoicePrintProfile(templateData);

          await backendApi.generateInvoicePDF(createdSaleId, selectedTemplateId, printProfile, {
            forceRegenerate: true
          });
          await backendApi.sendInvoicePdfToCustomer(createdSaleId, { full: 1 });
          successMessage = `${successMessage} Invoice PDF sent on WhatsApp.`;
        }

        setSuccess(successMessage);
      }

      handleCloseDialog();
      invalidateSalesSearchCache();

      loadInitialSalesData().catch(err => {
        console.error('Error refreshing sales data after sale:', err);
      });

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save sale. Please try again.');
      console.error('Error saving sale:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateInvoice = async (rowRef) => {
    try {
      if (invoiceGeneratingSaleId) {
        return;
      }

      const saleRow = typeof rowRef === 'number'
        ? (currentPageSales?.[rowRef] || allSales?.[rowRef] || sales?.[rowRef])
        : rowRef;
      if (!saleRow) {
        throw new Error('Sale data not found');
      }
      if (!saleRow.id) {
        throw new Error('Sale ID is missing for this row');
      }

      setInvoiceGeneratingSaleId(saleRow.id);
      console.log('Generating invoice for sale:', saleRow.id);

      const selectedTemplateId = selectedTemplate || templateData.theme || 'simple';
      const invoiceRequest = {
        rowNumber: saleRow.id,
        saleRow: saleRow,
        templateData: {
          ...templateData,
          selectedTemplate: selectedTemplateId
        },
        selectedTemplate: selectedTemplateId
      };

      const result = await backendApi.generateInvoice(invoiceRequest);
      if (!result?.invoice) {
        throw new Error('No invoice returned from server');
      }

      const normalizedInvoice = {
        ...result.invoice,
        invoiceId: result.invoice.invoiceId || result.invoice.invoiceNo || result.invoice.invoice_no,
        customerName: result.invoice.customerName || result.invoice.customer_name || saleRow['Customer Name'] || saleRow.customer_name || 'Walk-in Customer',
        phone: result.invoice.customerPhone || result.invoice.customer_phone || saleRow['Phone Number'] || saleRow.customer_phone || '',
        date: result.invoice.invoiceDate || result.invoice.invoice_date || saleRow['Date'] || saleRow.invoice_date || new Date().toLocaleDateString('en-IN'),
        subtotal: Number(result.invoice.totalAmount ?? result.invoice.total_amount ?? 0),
        totalPaid: Number(result.invoice.totalPaid ?? result.invoice.total_paid ?? 0),
        dueAmount: Number(result.invoice.dueAmount ?? result.invoice.due_amount ?? 0),
        htmlTemplate: result.invoice.htmlTemplate || ''
      };

      setGeneratedInvoice(normalizedInvoice);
      setShowInvoice(true);
      setSuccess('Invoice generated successfully!');
      await loadInitialSalesData();
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      setError('Failed to generate invoice. Please try again.');
      console.error('Error generating invoice:', err);
    } finally {
      setInvoiceGeneratingSaleId(null);
    }
  };

  const getGeneratedInvoicePdfBlob = async () => {
    if (!generatedInvoice?.id) {
      throw new Error('Invoice ID is missing');
    }
    return backendApi.downloadInvoicePDF(generatedInvoice.id);
  };

  const setInvoiceActionBusy = (action, value) => {
    setInvoiceActionLoading((prev) => ({ ...prev, [action]: value }));
  };

  const isAnyInvoiceActionBusy =
    invoiceActionLoading.download ||
    invoiceActionLoading.print ||
    invoiceActionLoading.whatsapp;

  // Pre-compute searchable data structures for better performance
  const searchSourceSales = useMemo(() => {
    if (fullDataLoaded && Array.isArray(fullSalesData)) {
      return fullSalesData;
    }
    return allSales;
  }, [allSales, fullDataLoaded, fullSalesData]);

  const searchableSales = useMemo(() => {
    return searchSourceSales.map(sale => ({
      ...sale,
      searchText: [
        sale['Customer Name'] || '',
        sale['Phone Number'] || '',
        sale['Item Name'] || '',
        sale['Date'] || '',
        sale['Payment Mode'] || '',
        String(sale['Total Amount'] || ''),
        String(sale['Final Amount'] || ''),
        String(sale['Total Paid'] || ''),
        String(sale['Due Amount'] || ''),
        ...(sale.items?.map(item => item.item_name || '') || [])
      ].join(' ').toLowerCase()
    }));
  }, [searchSourceSales]);

  // Memoized filtered sales - optimized with pre-computed search text
  const filteredSales = useMemo(() => {
    if (!deferredSearchTerm.trim()) {
      return [];
    }

    const searchLower = deferredSearchTerm.toLowerCase();
    return searchableSales.filter(sale =>
      sale.searchText.includes(searchLower)
    );
  }, [searchableSales, deferredSearchTerm]);

  const isSearchActive = debouncedSearchTerm.trim().length > 0;
  const totalVisibleSales = isSearchActive ? filteredSales.length : totalSalesCount;
  const displayFrom = totalVisibleSales === 0 ? 0 : ((currentPage - 1) * rowsPerPage) + 1;
  const displayTo = totalVisibleSales === 0 ? 0 : Math.min(currentPage * rowsPerPage, totalVisibleSales);

  // Conditionally handle pagination based on search mode
  const currentPageSales = useMemo(() => {
    // For search mode (client-side pagination), slice the filtered results
    if (isSearchActive) {
      const startIndex = (currentPage - 1) * rowsPerPage;
      return filteredSales.slice(startIndex, startIndex + rowsPerPage);
    }

    // For server-side pagination (non-search), use loaded page data directly
    // allSales already contains the correct page data from backend
    return allSales;
  }, [filteredSales, allSales, currentPage, rowsPerPage, isSearchActive]);

  const handleSearchChange = (event) => {
      const value = event.target.value;
      setSearchTerm(value);

      // Clear search if user deletes all text
      if (!value.trim()) {
        setDebouncedSearchTerm('');
        setCurrentPage(1);
      }
    };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const renderSalesRow = useCallback((sale, index) => (
    <SalesRow
      key={sale.id || `sale-${index}`}
      sale={sale}
      index={index}
      onEdit={handleOpenDialog}
      onGenerateInvoice={handleGenerateInvoice}
      rowIndex={index}
      navigate={navigate}
      isGeneratingInvoice={invoiceGeneratingSaleId === sale.id}
      disableInvoiceGeneration={Boolean(invoiceGeneratingSaleId)}
    />
  ), [handleOpenDialog, handleGenerateInvoice, navigate, invoiceGeneratingSaleId]);

  const emptySalesMessage = useMemo(
    () => (searchTerm ? 'No sales found matching your search.' : 'No sales yet. Add your first sale to get started.'),
    [searchTerm]
  );

  const handleRowsPerPageChange = useCallback(async (e) => {
    const newRowsPerPage = parseInt(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);

    if (!debouncedSearchTerm.trim()) {
      try {
        setLoading(true);
        setError(null);

        const salesResponse = await backendApi.getSales(1, newRowsPerPage);
        const salesData = salesResponse.sales || [];
        const totalSales = salesResponse.totalSales || 0;
        const normalizedSales = normalizeSalesData(salesData);

        setAllSales(normalizedSales);
        setTotalSalesCount(totalSales);
      } catch (err) {
        setError('Failed to load sales data with new page size. Please try again.');
        console.error('Error loading sales data:', err);
      } finally {
        setLoading(false);
      }
    }
  }, [debouncedSearchTerm, normalizeSalesData]);


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const filterCustomerOptions = useCallback((options, { inputValue }) => {
    if (!inputValue) return [];
    const normalizedInput = inputValue.toLowerCase();
    return options.filter((option) =>
      option.name.toLowerCase().includes(normalizedInput)
    );
  }, []);

  const handleCustomerInputChange = useCallback((event, newInputValue) => {
    handleInputChange('customerName', newInputValue);

    const matchingCustomer = customersByNormalizedName.get(newInputValue.toLowerCase());
    if (matchingCustomer) {
      handleInputChange('customerId', matchingCustomer.id);
      handleInputChange('phoneNumber', matchingCustomer.phone || '');
      setSelectedCustomerCredit(parseFloat(matchingCustomer.credit_balance || 0));
      return;
    }

    if (newInputValue !== (selectedCustomerOption?.name || '')) {
      handleInputChange('customerId', null);
      handleInputChange('phoneNumber', '');
      setSelectedCustomerCredit(0);
    }
  }, [customersByNormalizedName, selectedCustomerOption]);

  const handleCustomerChange = useCallback((event, newValue) => {
    if (newValue && typeof newValue === 'object' && newValue.id) {
      handleInputChange('customerId', newValue.id);
      handleInputChange('customerName', newValue.name);
      handleInputChange('phoneNumber', newValue.phone || '');
      setSelectedCustomerCredit(parseFloat(newValue.credit_balance || 0));
      return;
    }

    handleInputChange('customerId', null);
    handleInputChange('customerName', newValue || '');
    handleInputChange('phoneNumber', '');
    setSelectedCustomerCredit(0);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Page Header (NOT scrollable) */}
      <Box sx={{ flexShrink: 0 }}>
      {/* Header section with title and total sales card side by side */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {/* Left side - Title, description, search, and button */}
        <Box sx={{ flex: 1, mr: 3, display: 'flex', flexDirection: 'column' }}>
          {/* Header with title */}
          <Typography variant="h4" gutterBottom>
            Sales Management
          </Typography>

          {/* Description */}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Track and manage all your sales transactions.
          </Typography>

          {/* Search Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <TextField
              placeholder="Search sales by customer, phone, items, date, amount, or payment..."
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              sx={{ flex: 1, maxWidth: 500 }}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                ),
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loadingFullData && (
                      <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                    )}
                    {searchTerm && (
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon />
                      </IconButton>
                    )}
                  </Box>
                ),
              }}
            />
            {searchTerm && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Found {filteredSales.length} of {allSales.length} sales
                </Typography>
                {loadingFullData && (
                  <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                    Loading full data...
                  </Typography>
                )}
              </Box>
            )}

          </Box>

          {/* Add Sale Button - positioned at bottom right */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={saving}
              size="small"
            >
              Add Sale
            </Button>
          </Box>
        </Box>

        {/* Right side - Total Sales Card */}
        <Box sx={{ flexShrink: 0, width: 280 }}>
          <Card sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)'
          }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Total Sales
              </Typography>
              <TotalSalesValue
                totalValue={totalSalesValue}
                totalCount={totalSalesCount}
                searchTerm={searchTerm}
                filteredCount={filteredSales.length}
              />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                From all sales transactions
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {(error || success || invoiceGeneratingSaleId) && (
        <Box sx={{ mb: 2 }}>
          {invoiceGeneratingSaleId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Generating invoice...
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
        </Box>
      )}
      </Box>

      {/* Table Container (flex: 1; overflow: hidden) */}
      <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <DataTable
          columns={SALES_COLUMNS}
          data={currentPageSales}
          renderRow={renderSalesRow}
          onEdit={handleOpenDialog}
          minWidth="800px"
          loading={loading}
          emptyMessage={emptySalesMessage}
        />
      </Box>

      {/* Pagination Footer (fixed height, not scrollable) */}
      <Box sx={{ flexShrink: 0, pt: 2 }}>
        {/* Rows per page selector below table */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 70 }}>
            <Select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Showing {displayFrom}-{displayTo} of {totalVisibleSales} sales
          {isSearchActive ? ` (filtered from ${totalSalesCount} total)` : ''}
          {!fullDataLoaded && !loading && !loadingFullData && ` • ${allSales.length} loaded for fast display`}
          {loadingFullData && ' • Loading full dataset...'}
        </Typography>

        <Pagination
          count={Math.max(1, Math.ceil(totalVisibleSales / rowsPerPage))}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          sx={{ display: 'flex', justifyContent: 'flex-end' }}
        />
      </Box>

      </Box>
      {/* Add/Edit Sale Dialog */}
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        keepMounted
        TransitionProps={{ timeout: { enter: 140, exit: 100 } }}
      >
        <DialogTitle>{editingSale ? 'Edit Sale' : 'Add New Sale'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Date"
            type="date"
            fullWidth
            variant="outlined"
            value={formData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Box display="flex" gap={2} mt={2}>
            <Autocomplete
              fullWidth
              freeSolo
              options={customers}
              filterOptions={filterCustomerOptions}
              getOptionLabel={(option) => {
                // Handle both string and object options
                if (typeof option === 'string') {
                  return option;
                }
                return option.name || '';
              }}
              value={selectedCustomerOption}
              inputValue={formData.customerName}
              onInputChange={handleCustomerInputChange}
              onChange={handleCustomerChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer Name"
                  variant="outlined"
                  placeholder="Type to search customers or enter new name"
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={option.id || option.name} {...otherProps}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option.name}
                      </Typography>
                      {option.phone && (
                        <Typography variant="body2" color="text.secondary">
                          📞 {option.phone}
                        </Typography>
                      )}
                      {option.address && (
                        <Typography variant="body2" color="text.secondary">
                          📍 {option.address}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
            />

            <TextField
              label="Phone Number"
              fullWidth
              variant="outlined"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              placeholder="Enter phone number"
            />
          </Box>

          {/* Items Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Items
            </Typography>

            {/* Direct Item Search */}
            <Box sx={{ mb: 2, position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Type to search and add items..."
                value={itemSearchTerm}
                onChange={(e) => handleItemSearch(e.target.value)}
                variant="outlined"
                size="small"
                autoComplete="off"
              />
              {itemSearchTerm && (itemSearchResultsMemo.length > 0 || inventoryLoading) && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    maxHeight: 300,
                    overflow: 'auto',
                    mt: 0.5,
                    boxShadow: 3,
                  }}
                >
                  {inventoryLoading ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Loading inventory...
                      </Typography>
                    </Box>
                  ) : (
                    itemSearchResultsMemo.map((item) => {
                      const itemName = item.name;
                      const isSelected = selectedItemIds.has(item.id);
                      return (
                        <MenuItem
                          key={item.id}
                          onClick={() => !isSelected && handleItemSelect(item)}
                          sx={{
                            py: 1,
                            opacity: isSelected ? 0.5 : 1,
                            pointerEvents: isSelected ? 'none' : 'auto'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {itemName}
                              </Typography>
                              {item.sku && (
                                <Typography variant="caption" color="text.secondary">
                                  SKU: {item.sku}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                ₹{parseFloat(item.selling_price || 0).toLocaleString('en-IN')}
                              </Typography>
                              {isSelected && (
                                <Chip
                                  label="Selected"
                                  size="small"
                                  color="success"
                                />
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      );
                    })
                  )}
                </Paper>
              )}
            </Box>

            {/* Items Table */}
            {selectedItems.length > 0 && (
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Sale Price</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Customer Price</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>Discount (%)</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 50 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedItems.map(renderSelectedItemRow)}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>


          <Box display="flex" gap={2} mt={2}>
            <TextField
              label="Total Paid"
              type="number"
              variant="outlined"
              value={formData.totalPaid}
              onChange={(e) => handleInputChange('totalPaid', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
              sx={{ flex: 1 }}
            />

            <TextField
              label="Payment Mode"
              select
              variant="outlined"
              value={formData.paymentMode}
              onChange={(e) => handleInputChange('paymentMode', e.target.value)}
              sx={{ flex: 1 }}
            >
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="Card">Card</MenuItem>
              <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
            </TextField>
          </Box>

          {/* Credit Information Section */}
          {formData.customerId && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: selectedCustomerCredit > 0 ? '#e8f5e8' : '#f5f5f5', borderRadius: 2, border: `1px solid ${selectedCustomerCredit > 0 ? '#4caf50' : '#bdbdbd'}` }}>
              <Typography variant="h6" gutterBottom sx={{ color: selectedCustomerCredit > 0 ? 'success.main' : 'text.primary', fontWeight: 600 }}>
                💰 Customer Credit Balance
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: selectedCustomerCredit > 0 ? 'success.dark' : 'text.secondary' }}>
                {selectedCustomerCredit > 0 ? (
                  <>
                    This customer has ₹{selectedCustomerCredit.toLocaleString('en-IN')} credit available from previous overpayments.
                    This credit will be automatically applied to this sale.
                  </>
                ) : (
                  'This customer has no credit balance available.'
                )}
              </Typography>

              {selectedCustomerCredit > 0 && (
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                  ✅ ₹{selectedCustomerCredit.toLocaleString('en-IN')} credit will be automatically applied to this sale
                </Typography>
              )}
            </Box>
          )}

          {/* Summary Section */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    TOTAL
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                    ₹{formCalculations.total.toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    TOTAL DISCOUNT
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
                    ₹{formCalculations.totalDiscount.toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    PAYMENT BREAKDOWN
                  </Typography>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                      Cash: ₹{(formData.totalPaid || 0).toLocaleString('en-IN')}
                    </Typography>
                    {formData.customerId && selectedCustomerCredit > 0 && (
                      <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 600 }}>
                        Credit (Auto): ₹{selectedCustomerCredit.toLocaleString('en-IN')}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                      Total Paid: ₹{formCalculations.totalPaid.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    TOTAL DUE
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 700 }}>
                    ₹{formCalculations.due.toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 2 }}>
            <TextField
              label="Notes"
              multiline
              rows={2}
              variant="outlined"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes for this sale"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          {!editingSale && (
            <Button
              onClick={() => handleSubmit('save_and_send')}
              variant="contained"
              color="success"
              disabled={saving}
              startIcon={<WhatsAppIcon />}
            >
              {saving ? 'Saving...' : 'Save & Send WhatsApp'}
            </Button>
          )}
          <Button onClick={() => handleSubmit('save')} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : (editingSale ? 'Update Sale' : 'Add Sale')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Display Dialog */}
      <Dialog
        open={showInvoice}
        onClose={() => setShowInvoice(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          Generated Invoice - {generatedInvoice?.invoiceId}
          <Box sx={{ mt: 1 }}>
            <Button
              onClick={async () => {
                setInvoiceActionBusy('download', true);
                try {
                  const printProfile = resolveInvoicePrintProfile(templateData);
                  if (generatedInvoice?.id) {
                    const blob = await getGeneratedInvoicePdfBlob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Invoice-${generatedInvoice.invoiceId || generatedInvoice.id}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    setSuccess('PDF downloaded successfully!');
                    setTimeout(() => setSuccess(null), 2000);
                    return;
                  }

                  if (generatedInvoice?.htmlTemplate) {
                    const opt = getInvoiceHtml2PdfOptions(
                      printProfile,
                      `${generatedInvoice.invoiceId || 'invoice'}.pdf`
                    );
                    await html2pdf().set(opt).from(generatedInvoice.htmlTemplate).save();
                    setSuccess('PDF downloaded successfully!');
                    setTimeout(() => setSuccess(null), 2000);
                    return;
                  }

                  setError('No invoice template found. Please generate the invoice first.');
                } catch (error) {
                  console.error('Error downloading invoice PDF:', error);
                  if (generatedInvoice?.htmlTemplate) {
                    try {
                      const printProfile = resolveInvoicePrintProfile(templateData);
                      const opt = getInvoiceHtml2PdfOptions(
                        printProfile,
                        `${generatedInvoice.invoiceId || 'invoice'}.pdf`
                      );
                      await html2pdf().set(opt).from(generatedInvoice.htmlTemplate).save();
                    } catch (fallbackError) {
                      console.error('Fallback PDF generation failed:', fallbackError);
                      setError('Failed to generate PDF. Please try again.');
                    }
                  } else {
                    setError('Failed to generate PDF. Please try again.');
                  }
                } finally {
                  setInvoiceActionBusy('download', false);
                }
              }}
              variant="contained"
              size="small"
              sx={{ mr: 1 }}
              disabled={isAnyInvoiceActionBusy || !generatedInvoice}
            >
              {invoiceActionLoading.download ? 'Downloading...' : 'Download PDF'}
            </Button>

            <Button
              onClick={async () => {
                setInvoiceActionBusy('print', true);
                try {
                  const printProfile = resolveInvoicePrintProfile(templateData);
                  if (generatedInvoice?.htmlTemplate) {
                    const htmlDoc = `<!doctype html><html><head><meta charset="utf-8" />
                      <meta name="viewport" content="width=device-width, initial-scale=1" />
                      <style>${getInvoicePrintCss(printProfile)}</style>
                    </head><body>${generatedInvoice.htmlTemplate}
                      <script>window.onload=function(){setTimeout(function(){window.focus();window.print();},50)}<\/script>
                    </body></html>`;
                    const printWin = window.open('', '_blank');
                    if (printWin) {
                      printWin.document.open();
                      printWin.document.write(htmlDoc);
                      printWin.document.close();
                      return;
                    }
                  }

                  const blob = await getGeneratedInvoicePdfBlob();
                  const url = window.URL.createObjectURL(blob);
                  const win = window.open(url, '_blank');
                  if (win) {
                    win.onload = () => win.print();
                  }
                  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
                } catch (error) {
                  console.error('Error printing invoice PDF:', error);
                  if (generatedInvoice?.htmlTemplate) {
                    try {
                      const printProfile = resolveInvoicePrintProfile(templateData);
                      const opt = getInvoiceHtml2PdfOptions(
                        printProfile,
                        `${generatedInvoice.invoiceId || 'invoice'}.pdf`
                      );
                      const pdfBlob = await html2pdf().set(opt).from(generatedInvoice.htmlTemplate).outputPdf('blob');
                      const pdfUrl = URL.createObjectURL(pdfBlob);
                      const printWindow = window.open(pdfUrl, '_blank');
                      if (printWindow) {
                        printWindow.onload = () => {
                          printWindow.print();
                        };
                      }
                    } catch (fallbackError) {
                      console.error('Fallback print generation failed:', fallbackError);
                      setError('Failed to generate PDF for printing. Please try again.');
                    }
                  } else {
                    setError('Failed to generate PDF for printing. Please try again.');
                  }
                } finally {
                  setInvoiceActionBusy('print', false);
                }
              }}
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
              disabled={isAnyInvoiceActionBusy || !generatedInvoice}
            >
              {invoiceActionLoading.print ? 'Preparing...' : 'Print'}
            </Button>

            <Button
              onClick={async () => {
                setInvoiceActionBusy('whatsapp', true);
                try {
                  const phone = (generatedInvoice?.phone || '').replace(/\D/g, '');
                  if (!phone) {
                    setShowInvoice(false);
                    setError('No phone number found for this customer. Please add a phone number to share via WhatsApp.');
                    return;
                  }

                  const message = `Invoice Details\n\n` +
                    `Invoice ID: ${generatedInvoice.invoiceId || generatedInvoice.id}\n` +
                    `Customer: ${generatedInvoice.customerName}\n` +
                    `Date: ${generatedInvoice.date ? new Date(generatedInvoice.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}\n` +
                    `Total Amount: Rs.${generatedInvoice.subtotal?.toFixed(2) || '0.00'}\n` +
                    `Amount Paid: Rs.${generatedInvoice.totalPaid?.toFixed(2) || '0.00'}\n` +
                    `Amount Due: Rs.${generatedInvoice.dueAmount?.toFixed(2) || '0.00'}\n` +
                    `Payment Terms: ${generatedInvoice.paymentTerms || 'Net 30'}`;

                  const businessName =
                    businessData?.name ||
                    businessData?.companyName ||
                    'Dhandhapaani';

                  if (generatedInvoice?.id) {
                    try {
                      await backendApi.sendInvoicePdfToCustomer(generatedInvoice.id, {
                        caption: `Invoice ${generatedInvoice.invoiceId || generatedInvoice.id}`,
                        footer: businessName,
                        full: 1
                      });
                      setSuccess('Invoice PDF sent on WhatsApp successfully.');
                      setTimeout(() => setSuccess(null), 3000);
                      return;
                    } catch (apiError) {
                      console.warn('Direct WhatsApp PDF send failed:', apiError);
                      const apiMessage = apiError?.response?.data?.message || apiError?.message || 'Failed to send invoice PDF on WhatsApp.';
                      setError(apiMessage);
                      setTimeout(() => setError(''), 5000);
                      return;
                    }
                  }

                  setError('Invoice ID missing. Unable to send PDF.');
                  setTimeout(() => setError(''), 4000);
                } catch (error) {
                  console.error('Error sharing via WhatsApp:', error);
                  setError('Failed to share via WhatsApp. Please try again.');
                } finally {
                  setInvoiceActionBusy('whatsapp', false);
                }
              }}
              variant="outlined"
              size="small"
              color="success"
              sx={{ mr: 1 }}
              disabled={isAnyInvoiceActionBusy || !generatedInvoice}
            >
              {invoiceActionLoading.whatsapp ? 'Preparing...' : 'Share WhatsApp'}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {generatedInvoice?.htmlTemplate && (
            <div
              dangerouslySetInnerHTML={{ __html: generatedInvoice.htmlTemplate }}
              style={{ width: '100%', height: '100%' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInvoice(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Sales;









